import { useEffect, useState } from "react";
import { motion } from "framer-motion";

export default function SwapSpitModal({ game, myRole, allCards, onReady, swapProgress, clock }) {
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [ready, setReady] = useState(false);

  // Server-authoritative countdown — re-derived each tick from started_at +
  // duration so a brief tab background / WS reconnect doesn't desync the timer.
  const [secondsLeft, setSecondsLeft] = useState(() => {
    if (!clock?.started_at || !clock?.duration) return null;
    const remaining = (clock.started_at + clock.duration * 1000 - Date.now()) / 1000;
    return Math.max(0, Math.ceil(remaining));
  });
  useEffect(() => {
    if (!clock?.started_at || !clock?.duration) {
      setSecondsLeft(null);
      return;
    }
    const tick = () => {
      const remaining = (clock.started_at + clock.duration * 1000 - Date.now()) / 1000;
      setSecondsLeft(Math.max(0, Math.ceil(remaining)));
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [clock?.started_at, clock?.duration]);

  const myHand = (myRole === "home" ? game?.home_hand : game?.away_hand) || [];
  const mySpent = new Set((myRole === "home" ? game?.home_spent_cards : game?.away_spent_cards) || []);
  // Active cards live at the top level of `game`, not under `state` — the wrong
  // path here previously suppressed the "Active" badge and miscalculated the
  // spent-but-active swappable carve-out.
  const myActiveIds = new Set(
    ((myRole === "home" ? game?.home_active_cards : game?.away_active_cards) || [])
      .filter(c => c && !c.burned)
      .map(c => c?.id).filter(Boolean)
  );

  const cardById = (id) => allCards?.find(c => c.id === id);

  const swappable = myHand.filter(c => {
    const id = c?.id || c;
    return (!mySpent.has(id) || myActiveIds.has(id)) && id !== "swap_spit";
  });

  // Swap Spit cards in hand are always excluded — you can't swap the card
  // you're using to make the swap. Show them grayed out so the player sees why.
  const swapSpitInHand = myHand.filter(c => (c?.id || c) === "swap_spit");

  const oppRole = myRole === "home" ? "away" : "home";
  const oppReady = swapProgress.includes(oppRole);

  const handleReady = () => {
    if (!selectedCardId || ready) return;
    setReady(true);
    onReady(selectedCardId);
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-3">
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        // max-h-[90vh] keeps the modal inside the window on shorter viewports;
        // the inner card list owns its own overflow so the header + footer
        // (countdown / Ready button) stay anchored.
        className="bg-slate-900 border border-purple-500/40 rounded-2xl p-5 w-full max-w-sm shadow-2xl flex flex-col max-h-[90vh]"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-black uppercase tracking-widest text-purple-400">Swap Spit</span>
          {secondsLeft != null ? (
            <span
              className={`font-mono font-black tabular-nums text-sm ${
                secondsLeft <= 5 ? "text-red-400 animate-pulse" : "text-slate-300"
              }`}
              title="Auto-pick when this hits 0"
            >
              {secondsLeft}s
            </span>
          ) : (
            <span className="text-xs text-slate-400">Pick 1 card to swap</span>
          )}
        </div>

        <div className="space-y-2 mb-4 flex-1 min-h-0 overflow-y-auto">
          {swappable.length === 0 && swapSpitInHand.length === 0 && (
            <p className="text-slate-500 text-sm text-center py-4">No swappable cards.</p>
          )}
          {swappable.map(c => {
            const id = c?.id || c;
            const meta = (typeof c === "object" && c?.name) ? c : cardById(id);
            const name = meta?.name || id;
            const desc = meta?.description || "";
            const isActive = myActiveIds.has(id);
            const isSelected = selectedCardId === id;
            return (
              <button
                key={id}
                /* Namespaced swap-card-*: this modal is position:fixed z-index:1000
                   and renders its OWN card buttons. Without a testid here,
                   [data-testid=card-<id>] resolved to the tray copies underneath
                   the overlay, so a click selected nothing and the confirm stayed
                   disabled — a seat lost ten minutes to it and filed a blocker. */
                data-testid={`swap-card-${id}`}
                data-card-id={id}
                data-selected={String(selectedCardId === id)}
                onClick={() => !ready && setSelectedCardId(id)}
                disabled={ready}
                className={`w-full text-left rounded-xl border p-3 transition-all ${
                  isSelected
                    ? "border-purple-500/70 bg-purple-500/15"
                    : isActive
                    ? "border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-400/50"
                    : "border-slate-700 bg-slate-800/60 hover:border-slate-500"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-black text-white">{name}</span>
                  {isActive && (
                    <span className="text-[8px] font-black px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 uppercase tracking-wider">
                      Active
                    </span>
                  )}
                  {isSelected && (
                    <span className="text-[8px] font-black px-1.5 py-0.5 rounded bg-purple-500/20 border border-purple-500/40 text-purple-300 uppercase tracking-wider">
                      Selected
                    </span>
                  )}
                </div>
                {desc && <p className="text-xs text-slate-500 mt-1">{desc}</p>}
              </button>
            );
          })}
          {swapSpitInHand.map(c => {
            const id = c?.id || c;
            const meta = (typeof c === "object" && c?.name) ? c : cardById(id);
            const name = meta?.name || id;
            const desc = meta?.description || "";
            return (
              <div
                key={`spit-${id}`}
                className="w-full text-left rounded-xl border p-3 border-slate-700/40 bg-slate-800/20 opacity-40 cursor-not-allowed"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-black text-slate-400">{name}</span>
                  <span className="text-[8px] font-black px-1.5 py-0.5 rounded bg-slate-700/40 border border-slate-600/30 text-slate-500 uppercase tracking-wider">
                    Used
                  </span>
                </div>
                {desc && <p className="text-xs text-slate-600 mt-1">{desc}</p>}
              </div>
            );
          })}
        </div>

        {oppReady && !ready && (
          <p className="text-xs text-amber-400 font-bold text-center mb-2">Opponent is ready ✓</p>
        )}

        {ready ? (
          <div className="text-center py-2">
            <p className="text-emerald-400 font-black text-sm">✓ Ready — waiting for opponent…</p>
            <div className="w-5 h-5 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin mx-auto mt-2" />
          </div>
        ) : (
          <button
            onClick={handleReady}
            data-testid="swap-spit-ready"
            disabled={!selectedCardId}
            className="w-full py-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-black text-sm transition disabled:opacity-40"
          >
            {selectedCardId ? "Ready Up →" : "Select a card first"}
          </button>
        )}
      </motion.div>
    </div>
  );
}
